import "./index.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { TRPCClientError, httpSubscriptionLink, splitLink } from "@trpc/client";
import { httpBatchLink } from "@trpc/react-query";
import { TOKEN_STORAGE_KEY, tokenManager } from "lib/tokenManager";
import { trpc } from "lib/trpc";
import { SnackbarProvider } from "notistack";
import * as React from "react";
import { ProseRenderConfigProvider } from "@compass/shared/components/TipTapRenderer";
import { createAuthRetryFetch } from "@compass/shared/utils/authRetryFetch";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { webProseComponents } from "./components/prose/webProseComponents";
import { SessionRefresh } from "./components/SessionRefresh";
import { UpdateBanner } from "./components/UpdateBanner";
import { API_BASE } from "./config";
import { router } from "./router";

function clearTokenAndRedirect() {
    tokenManager.set(null);
    window.location.href = "/login";
}

function isUnauthorizedError(error: unknown): boolean {
    if (error instanceof TRPCClientError) {
        const code = error.data?.code;
        const httpStatus = error.data?.httpStatus;
        if (code === "UNAUTHORIZED" || httpStatus === 401) return true;
        if (error.message.toLowerCase().includes("token")) return true;
    }
    return false;
}

// PRTL-1519: re-sends a request once when a refresh replaced the token it carried.
const fetchWithAuthRetry = createAuthRetryFetch({
    fetch: (url, init) => fetch(url, init),
    getToken: tokenManager.get,
    waitForRefresh: tokenManager.waitForRefresh,
});

const snackbarOptions = {
    maxSnack: 3,
    autoHideDuration: 3000,
    preventDuplicate: true,
    hideIconVariant: true,
};

export const App = () => {
    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        staleTime: 2 * 60 * 1000,
                        gcTime: 10 * 60 * 1000,
                        retry(failureCount, error) {
                            if (isUnauthorizedError(error)) {
                                clearTokenAndRedirect();
                                return false;
                            }
                            return failureCount < 3;
                        },
                    },
                    mutations: {
                        onError(error) {
                            if (isUnauthorizedError(error)) {
                                clearTokenAndRedirect();
                            }
                        },
                    },
                },
            }),
    );
    const token = React.useSyncExternalStore(tokenManager.subscribe, tokenManager.get);
    const trpcClient = React.useMemo(
        () =>
            trpc.createClient({
                links: [
                    splitLink({
                        condition: (op) => op.type === "subscription",
                        true: httpSubscriptionLink({
                            url: `${API_BASE}/trpc`,
                            connectionParams: () => {
                                if (!token) return {};
                                return { Authorization: `Bearer ${token}` };
                            },
                        }),
                        false: httpBatchLink({
                            url: `${API_BASE}/trpc`,
                            headers() {
                                if (!token) return {};
                                return {
                                    Authorization: `Bearer ${token}`,
                                };
                            },
                            // custom fetch
                            async fetch(url, options) {
                                const response = await fetchWithAuthRetry(String(url), {
                                    method: options?.method,
                                    headers: options?.headers as HeadersInit,
                                    body: options?.body as BodyInit,
                                });
                                if (response.status === 401) {
                                    clearTokenAndRedirect();
                                }
                                return response;
                            },
                        }),
                    }),
                ],
            }),
        [token],
    );

    React.useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key !== TOKEN_STORAGE_KEY) return;
            if (event.newValue === null) {
                queryClient.clear();
                window.location.href = "/login";
            } else if (event.newValue !== tokenManager.get()) {
                // PRTL-1348: another tab switched orgs (its old token — ours —
                // is now revoked). Reload to adopt the new token and repaint
                // under the new org instead of 401ing into a logout.
                window.location.reload();
            }
        };

        window.addEventListener("storage", handleStorage);

        return () => {
            window.removeEventListener("storage", handleStorage);
        };
    }, [queryClient]);

    return (
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>
                <SnackbarProvider {...snackbarOptions}>
                    <ProseRenderConfigProvider config={webProseComponents}>
                        <ErrorBoundary>
                            <SessionRefresh />
                            <UpdateBanner />
                            <RouterProvider router={router} />
                        </ErrorBoundary>
                    </ProseRenderConfigProvider>
                </SnackbarProvider>
            </QueryClientProvider>
        </trpc.Provider>
    );
};
